import {useAppContext} from 'contexts';
import {jobActionHandlers, jobSearch, viewPdf, PDFMenu} from 'hooks';
import {pdfOptionsOrder} from 'shared/helpers';
import {JobStatusEnum} from 'shared/JobStatus';
import {PdfType, usePdfLoadingPrompt} from 'shared/helpers/usePdfLoadingPrompt';
import {Job} from 'shared/types/PartialJob';
import {useCallback, useMemo} from 'react';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {useNavigate} from 'react-router-dom';

const jobButtonsHandler = (isSearch = false) => {
    const navigate = useNavigate();
    const {userProfile} = useAppContext();
    const {
        copyJobHandler,
        unSubmitJobHandler,
        dialog,
        deleteJobHandler,
        editJobHandler,
        showJobStatus,
    } = jobActionHandlers();
    const {resetResults} = jobSearch();

    const {viewPdf: viewRoomPlannerPdf, pdfDialog} = usePdfLoadingPrompt(
        PdfType.ROOM_PLANNER,
        false
    );

    const dropdownMenuHandler = useCallback(
        (row: {id: number}, event: React.ChangeEvent, option: string) => {
            viewPdf(row, option);
        },
        []
    );

    const isHidden = useCallback(
        (row: Job) => userProfile?.inActiveManufacturer || row.resetPriceStatus,
        [userProfile]
    );

    const editableStatus = useMemo(
        () => [
            0, // Processing
            5, // Pending confirmation
            11, // Legacy GFP processing
        ],
        []
    );

    const buttons = useMemo(() => {
        return {
            title: 'Options',
            width: 50,
            buttons: [
                {
                    iconName: 'Invoice-Button.svg',
                    title: 'View invoice',
                    isHidden: (row: Job) => {
                        if (
                            row.status == JOB_STATUS.PENDING_APPROVAL ||
                            row.status == JOB_STATUS.PENDING_PAYMENT ||
                            row.status == JOB_STATUS.CUSTOMER_APPROVED
                        ) {
                            if (
                                row.paymentMethod == 'invoice' &&
                                Number(row.amountPaid) == 0
                            ) {
                                return false;
                            }

                            return true;
                        }

                        return true;
                    },
                    action: (row: Job, event: React.MouseEvent) => {
                        event.preventDefault();
                        const invoiceType =
                            row?.selectedInvoicePaymentType || 'full';

                        void navigate(
                            `/v2/job/${row.id + 10000}/invoice?narrow-header=1&payment-method=invoice_${invoiceType}`
                        );
                    },
                },
                {
                    iconName: 'Pay-Balance.svg',
                    title: 'Pay balance',
                    isHidden: (row: Job) => {
                        if (row?.paymentMethod == 'account') {
                            return true;
                        }

                        if (
                            row.status == JOB_STATUS.PENDING_APPROVAL ||
                            row.status == JOB_STATUS.PENDING_PAYMENT ||
                            row.status == JOB_STATUS.CUSTOMER_APPROVED
                        ) {
                            if (Number(row.amountDue) > 0) {
                                if (
                                    row.paymentMethod == 'invoice' &&
                                    Number(row.amountPaid) == 0
                                ) {
                                    return true;
                                }

                                return false;
                            }
                            return true;
                        }

                        return true;
                    },
                    action: (row: Job, event: React.MouseEvent) => {
                        event.preventDefault();
                        void navigate(`/v2/job/${row.id + 10000}/balance`);
                    },
                },
                {
                    iconName: 'Paid.svg',
                    title: 'Paid',
                    isHidden: (row: Job) => {
                        if (
                            row.status == JOB_STATUS.PENDING_APPROVAL ||
                            row.status == JOB_STATUS.CUSTOMER_APPROVED
                        ) {
                            if (
                                row.amountDue == null ||
                                Number(row.amountDue) > 0
                            ) {
                                return true;
                            }

                            return false;
                        }

                        return true;
                    },
                    action: () => {
                        //
                    },
                },
                {
                    iconName: 'Options-Edit.svg',
                    title: 'Edit Job',
                    rowAction: true,
                    action: (row: Job, event: React.MouseEvent) => {
                        event.stopPropagation();
                        editJobHandler(row);
                        if (isSearch) resetResults(true);
                    },
                    isHidden: (row: Job) => {
                        return editableStatus.includes(row.status) &&
                            !(
                                userProfile?.inActiveManufacturer ||
                                row.resetPriceStatus
                            )
                            ? false
                            : true;
                    },
                },
                {
                    iconName: 'Button-Rotate-Anti-Clockwise.svg',
                    title: 'Unsubmit Job',
                    rowAction: true,
                    action: (row: Job, event: React.MouseEvent) => {
                        event.stopPropagation();
                        unSubmitJobHandler(row);
                    },
                    isHidden: (row: Job) => {
                        return row.status == JOB_STATUS.PENDING_APPROVAL &&
                            !(
                                userProfile?.inActiveManufacturer ||
                                row.resetPriceStatus
                            )
                            ? row.amountDue == 0 || (row.amountPaid ?? 0) > 0
                            : true;
                    },
                },
                {
                    iconName: 'More-Info.svg',
                    title: 'Job Status',
                    rowAction: true,
                    action: (
                        row: {id: number; webhookResponse: string[]},
                        event: React.MouseEvent
                    ) => {
                        event.stopPropagation();
                        showJobStatus(row);
                    },
                    isHidden: (row: {
                        status: number;
                        webhookResponse: string[];
                    }) => {
                        return row.status > 0 && row.webhookResponse
                            ? false
                            : true;
                    },
                },
                {
                    iconName: 'Options-Copy.svg',
                    title: 'Copy Job',
                    action: (row: Job, event: React.MouseEvent) => {
                        event.stopPropagation();
                        copyJobHandler(row);
                    },
                    isHidden,
                },
                {
                    iconName: 'Options-PDF.svg',
                    isHidden: (row: Job) => {
                        return row.resetPriceStatus;
                    },
                    title: 'Get PDF',
                    dropdown: [
                        {
                            name: PDFMenu.JOB_DETAILS_PDF,
                            show:
                                userProfile?.hasOwnProperty(
                                    'showJobPropertiesTablePdf'
                                ) && userProfile.showJobPropertiesTablePdf
                                    ? true
                                    : false,
                            order: pdfOptionsOrder(
                                PDFMenu,
                                PDFMenu.JOB_DETAILS_PDF,
                                userProfile
                            ),
                            source: 'pdf_attachments',
                            type: 'job_details_pdf',
                        },
                        {
                            name: PDFMenu.ORDER_ACKNOWLEDGEMENT_PDF,
                            show:
                                userProfile?.hasOwnProperty(
                                    'showOrderAcknowledgementPdf'
                                ) && userProfile.showOrderAcknowledgementPdf
                                    ? true
                                    : false,
                            order: pdfOptionsOrder(
                                PDFMenu,
                                PDFMenu.ORDER_ACKNOWLEDGEMENT_PDF,
                                userProfile
                            ),
                        },
                        {
                            name: PDFMenu.CUSTOMER_INVOICE_PDF,
                            show: (row: Job) =>
                                userProfile?.hasOwnProperty(
                                    'allowCustomerInvoicingPdf'
                                ) &&
                                userProfile.allowCustomerInvoicingPdf &&
                                row.status > JobStatusEnum.PENDING_APPROVAL &&
                                ![JobStatusEnum.GRP_PROCESSING].includes(
                                    row.status
                                )
                                    ? true
                                    : false,
                            order: 5,
                        },
                        {
                            name: PDFMenu.JOB_TOPDOWN_LAYOUT_PDF,
                            show:
                                !userProfile?.allow3DRoomPlanner &&
                                userProfile?.isTDLDAvailable == 1,
                            order: 4,
                            source: 'pdf_attachments',
                            type: 'tdld_job_details_pdf',
                        },
                        {
                            name: PDFMenu.ROOM_PLANNER_PDF,
                            show: userProfile?.allow3DRoomPlanner,
                            order: 6,
                            source: 'pdf_attachments',
                            type: 'room_planner_room_details_pdf',
                            identifier: 'room_id',
                            action: viewRoomPlannerPdf,
                        },
                    ],
                    action: dropdownMenuHandler,
                },
                {
                    iconName: 'Options-Delete.svg',
                    title: 'Delete Job',
                    action: (row: Job, event: React.MouseEvent) => {
                        event.stopPropagation();
                        deleteJobHandler(row);
                    },
                    isHidden: (row: Job) => {
                        return editableStatus.includes(row.status) &&
                            !(
                                userProfile?.inActiveManufacturer ||
                                row.resetPriceStatus
                            )
                            ? false
                            : true;
                    },
                },
                {
                    iconName: 'tail-spin.svg',
                    title: 'Prices being reset',
                    isHidden: (row: Job) => {
                        return !row.resetPriceStatus;
                    },
                    action: () => {
                        //
                    },
                },
            ],
        };
    }, [
        userProfile,
        isSearch,
        editableStatus,
        isHidden,
        editJobHandler,
        unSubmitJobHandler,
        showJobStatus,
        copyJobHandler,
        deleteJobHandler,
        resetResults,
        viewRoomPlannerPdf,
        dropdownMenuHandler,
        navigate,
    ]);

    return {buttons, dialog, pdfDialog};
};

export {jobButtonsHandler};
